import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ProductModel} from '../../entity/product.model';
import {ProductSaveService} from '../../../impl/service/product-save.service';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ObjectUtils} from '../../../../../shared/utils/object.utils';
import {ProductSavePayload} from '../../../impl/service/payload/ProductSave.payload';
import {LovValueModalOptions} from '../../../../common/lov/lov-value/api/model/lovValueModalOptions';
import {ProductFindOneByIdService} from '../../../impl/service/productFindOneById.service';
import {
    CreateUpdateDefaultComponent
} from "../../../../core/component/create-update-default/create-update-default.component";

@Component({
    selector: 'app-product-save',
    templateUrl: './product-save.component.html',
    viewProviders: [ProductFindOneByIdService,
        ProductSaveService]
})
export class ProductSaveComponent implements OnInit, OnChanges {
    formGroup: UntypedFormGroup;
    lovValueModalOptions: LovValueModalOptions = {
        title: 'Ajouter une catégorie de produit'
    };

    stockRegex: RegExp = /\d*,?\d?$/;


    @Input() id: number;
    @Input() returnUrl: string;
    @Input() inDialog = false;
    @Input() typeValue: string;
    @Input() categoryValue: string;
    @Input() hideCategoryInput = false;
    @Input() hideTypeValueInput = false;
    @Input() hideAdvancedInput = false;
    @Input() hideVatInputs = false;
    @Input() priceVatIncludedLabel: string;
    @Input() hideStock = false;
    @Input() codeLabel = 'Code';
    @Input() hideSaveButton = false;
    @Output() entitySavedEvent = new EventEmitter<ProductModel>();
    @ViewChild('createUpdateDefaultComponent') createUpdateDefaultComponent: CreateUpdateDefaultComponent<any, any, any>;
    constructor(public service: ProductSaveService,
                public findByIdServe: ProductFindOneByIdService,
                private formBuilder: UntypedFormBuilder) {

        this.formGroup = this.formBuilder.group({
            summary: new UntypedFormGroup({
                id: new UntypedFormControl(undefined)
            }),
            code: new UntypedFormControl(''),
            label: new UntypedFormControl('', Validators.required),
            invoiceLabel: new UntypedFormControl(undefined),
            description: new UntypedFormControl(undefined),
            comment: new UntypedFormControl(undefined),
            categoryValue: new UntypedFormControl(undefined, Validators.required),
            typeValue: new UntypedFormControl(undefined),
            active: new UntypedFormControl(true),

            taxPercentage: new UntypedFormControl(0),
            priceVatExcluded: new UntypedFormControl(0),
            priceVatIncluded: new UntypedFormControl(0),
            advanceVatIncluded: new UntypedFormControl(0),
            advanceVatExcluded: new UntypedFormControl(0),

            hasStock: new UntypedFormControl(false),
            stock: new UntypedFormControl(0),
            colorValue: new UntypedFormControl(undefined)
        });

        this.taxPercentageControl.valueChanges.subscribe(value => {
            this.updatePriceVatIncluded();
            this.updateAdvanceVatIncluded();
        });

        this.priceVatExcludedControl.valueChanges.subscribe(value => {
            this.priceVatExcludedControl.setValue(this.roundNumber(value), {onlySelf: true, emitEvent: false});
            this.updatePriceVatIncluded();
        });

        this.advanceVatExcludedControl.valueChanges.subscribe(value => {
            this.advanceVatExcludedControl.setValue(this.roundNumber(value), {onlySelf: true, emitEvent: false});
            this.updateAdvanceVatIncluded();
        });

        this.priceVatIncludedControl.valueChanges.subscribe(value => {
            const taxPercentage = this.taxPercentageControl.value;
            const ratio = 1 + (taxPercentage / 100);
            const calculatedValue = ratio !== 0 ? (value / ratio) : 0;
            // console.logByType'calculated value : ', calculatedValue);
            this.priceVatExcludedControl.setValue(this.roundNumber(calculatedValue), {
                onlySelf: true,
                emitEvent: false
            });
            this.priceVatIncludedControl.setValue(this.roundNumber(value), {onlySelf: true, emitEvent: false});
        });

        this.advanceVatIncludedControl.valueChanges.subscribe(value => {
            const taxPercentage = this.taxPercentageControl.value;
            const ratio = 1 + (taxPercentage / 100);
            const calculatedValue = ratio !== 0 ? (value / ratio) : 0;
            this.advanceVatExcludedControl.setValue(this.roundNumber(calculatedValue), {
                onlySelf: true,
                emitEvent: false
            });
            this.advanceVatIncludedControl.setValue(this.roundNumber(value), {onlySelf: true, emitEvent: false});
        });

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['typeValue']) {
            const control = this.formGroup.controls.typeValue;
            control.setValue(this.typeValue);
            this.typeValue ? control.disable() : control.enable();
        }
        if (changes['categoryValue']) {
            this.formGroup.get('categoryValue').setValue(this.categoryValue);
        }
    }

    get hasStock(): boolean {
        return this.formGroup.controls.hasStock.value;
    }



    get advanceVatExcludedControl(): UntypedFormControl {
        return this.formGroup.controls.advanceVatExcluded as UntypedFormControl;
    }

    get advanceVatIncludedControl(): UntypedFormControl {
        return this.formGroup.controls.advanceVatIncluded as UntypedFormControl;
    }

    get priceVatExcludedControl(): UntypedFormControl {
        return this.formGroup.controls.priceVatExcluded as UntypedFormControl;
    }

    get priceVatIncludedControl(): UntypedFormControl {
        return this.formGroup.controls.priceVatIncluded as UntypedFormControl;
    }

    get taxPercentageControl(): UntypedFormControl {
        return this.formGroup.controls.taxPercentage as UntypedFormControl;
    }

    ngOnInit() {
    }

    entityLoaded(entity: ProductModel) {
        const summary = entity.summary;
        this.formGroup.patchValue({
            ...summary,
            summary: {
                id: summary.id
            },
            advanceVatExcluded: entity.summary.advanceVatExcluded,
            advanceVatIncluded: entity.summary.advanceVatIncluded,
            comment: entity.comment,
            categoryValue: entity.summary.category?.value,
            typeValue: entity.summary.type?.value
        });
    }

    buildPayload = (): ProductSavePayload => {
        const payload = ObjectUtils.assign(new ProductSavePayload(), this.formGroup.value, []);
        payload.typeValue = this.formGroup.controls.typeValue.value;

        if (!this.hasStock) {
            payload.stock = undefined;
        }

        return payload;
    }

    entitySaved(entity: ProductModel) {
        if (this.entitySavedEvent) {
            this.entitySavedEvent.emit(entity);
        }
    }

    private updatePriceVatIncluded() {
        const taxPercentage = this.taxPercentageControl.value;
        const calculatedValue = this.priceVatExcludedControl.value * (1 + (taxPercentage / 100));
        this.priceVatIncludedControl.setValue(this.roundNumber(calculatedValue), {onlySelf: true, emitEvent: false});
    }

    private updateAdvanceVatIncluded() {
        const taxPercentage = this.taxPercentageControl.value;
        const calculatedValue = this.advanceVatExcludedControl.value * (1 + (taxPercentage / 100));
        this.advanceVatIncludedControl.setValue(this.roundNumber(calculatedValue), {onlySelf: true, emitEvent: false});
    }

    private roundNumber(nb: number): number {
        return Math.round(nb * 100) / 100;
    }

    save() {
        this.createUpdateDefaultComponent.save();
    }
}
